import React, { useEffect, useRef } from 'react';

interface AudioWaveformProps {
  analyserNode: AnalyserNode | null;
  isRecording?: boolean;
  audioElement?: HTMLAudioElement | null;
  color?: string;
  barCount?: number;
  barWidth?: number;
  barGap?: number;
  height?: number;
}

export const AudioWaveform: React.FC<AudioWaveformProps> = ({
  analyserNode,
  isRecording = false,
  audioElement = null,
  color = '#ffffff',
  barCount = 40,
  barWidth = 2,
  barGap = 2,
  height = 32,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Cap height at 24px
    const maxHeight = 24;

    let currentAnalyser = analyserNode;

    // If we have an audio element and we're not recording, set up playback visualization
    if (audioElement && !isRecording && !analyserNode) {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      if (!sourceRef.current) {
        try {
          sourceRef.current = audioContextRef.current.createMediaElementSource(audioElement);
          analyserRef.current = audioContextRef.current.createAnalyser();
          analyserRef.current.fftSize = 256;
          sourceRef.current.connect(analyserRef.current);
          analyserRef.current.connect(audioContextRef.current.destination);
          currentAnalyser = analyserRef.current;

          // Resume audio context if suspended (browser autoplay policy)
          if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
          }
        } catch (error) {
          console.error('Error setting up audio visualization:', error);
        }
      } else if (analyserRef.current) {
        // Reuse existing analyser if source already exists
        currentAnalyser = analyserRef.current;

        // Resume audio context if suspended
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }
      }
    }

    if (!currentAnalyser) return;

    const bufferLength = currentAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    // Set up canvas size once
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = maxHeight * dpr;
    ctx.scale(dpr, dpr);

    // Calculate bar metrics once
    const maxBars = Math.floor(rect.width / (barWidth + barGap));
    const actualBarCount = Math.min(barCount, maxBars);
    const totalBarWidth = actualBarCount * barWidth;
    const totalGapWidth = rect.width - totalBarWidth;
    const actualGap = actualBarCount > 1 ? totalGapWidth / (actualBarCount - 1) : 0;

    const draw = () => {
      if (!canvas || !ctx || !currentAnalyser) return;

      animationRef.current = requestAnimationFrame(draw);

      currentAnalyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, rect.width, maxHeight);

      for (let i = 0; i < actualBarCount; i++) {
        const index = Math.floor((i / actualBarCount) * bufferLength);
        const value = dataArray[index];
        const barHeight = (value / 255) * maxHeight;
        const x = i * (barWidth + actualGap);
        const y = (maxHeight - barHeight) / 2;

        ctx.fillStyle = color;
        ctx.fillRect(x, y, barWidth, barHeight || 2);
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyserNode, audioElement, isRecording, color, barCount, barWidth, barGap, height]);

  // Cleanup audio context when component unmounts
  useEffect(() => {
    return () => {
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: 'block', width: '100%', height: '24px', maxHeight: '24px', minHeight: '24px' }}
    />
  );
};
